import React, { useEffect, useContext, useRef, useState } from "react";
import ApplicationContext from "../../../context/index";
import CircularProgress from "@mui/material/CircularProgress";
import SectionTitle from "../../../components/sectionTitle";
import CallToAction from "../partials/cta";
import ConfirmationDialog from "./alert";
import { withStyles } from "@mui/styles";
import { useStyles } from "../indexFormStyles";
import { Alert, Box, Stack, Typography, TextField } from "@mui/material";
import { getRoute } from "../../../helpers/utils";
import { useHistory } from "react-router";
import {
  getStorageItem,
  removeStorageItem,
  setStorageItem,
} from "../../../helpers/utils";
import { postWebService } from "../../../helpers/server";
import { callBilldeskSdk } from "../../../helpers/payment";
import { buildWrapperTokenHeader } from "../../../helpers/lead";
import { Utils } from "../../../constants/utils";

function BuyflowPayment({
  classes: { containerButton, main, loaderBox, formGroup },
}) {
  const { state, dispatch } = useContext(ApplicationContext);
  const history = useHistory();
  const timerRef = useRef(null);

  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [isLoaded, setIsLoaded] = useState(true);
  const [message, setMessage] = useState(
    "Payment in being processed. Please do not refresh the page or use the back button."
  );
  const [modal, setModal] = useState({ open: false, confirm: false });
  const [countdown, setCountdown] = useState(300);
  const [buttonDisabled, setButtonDisabled] = useState(true);

  const [configLoaded, setConfigLoaded] = useState(false);
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [paymentBlock, setPaymentBlock] = useState({
    options: false,
    pay_now: false,
    payment_link: false,
  });
  const [isButtonClicked, setIsButtonClicked] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [resendPaymentLink, setResendPaymentLink] = useState(false);
  const [form, setForm] = useState(null);

  useEffect(() => {
    wrapperToken();
  }, []);

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const wrapperToken = async (config = true) => {
    try {
      const header = buildWrapperTokenHeader();
      const response = await postWebService("token.php", {}, header);

      setStorageItem("access-token", response.data.access_token);
      if (config) {
        fetchAppConfig();
      }
    } catch (error) {
      // console.log(error);
    }
  };

  const initiatePayment = (data = null, paymentConfig = null) => {
    let billdeskPopup = false;

    if (paymentConfig) {
      billdeskPopup =
        paymentConfig.pay_now == true && paymentConfig.payment_link == false;
    } else {
      billdeskPopup = true;
      togglePaymentBlock("pay_now", true);
    }

    if (billdeskPopup) {
      createBillDeskOrder();
    } else {
      const code = data?.code ?? "";
      const link = data?.link ?? false;

      if (code === 404) {
        togglePaymentBlock(link ? "payment_link" : "options", true);
      } else if (code === 400) {
        togglePaymentBlock(link ? "payment_link" : "pay_now", true);
      } else if (code === 200) {
        generatePaymentReceipt();
      } else if (code === 403) {
        togglePaymentBlock("options", true);
      }
    }
  };

  const togglePaymentBlock = (key = "", value = false) => {
    setErrorMessage("");

    const newBlock = {
      ...paymentBlock,
      options: false,
      pay_now: false,
      payment_link: false,
    }; // shallow copy
    newBlock[key] = value; // update the key

    setPaymentBlock(newBlock);
  };

  const cbSuccessFetchAppConfig = (config) => {
    checkPaymentStatus(config);

    // Commented this code, as this is not required now.
    /*
    const keyname = `paymentretry${getStorageItem("oid")}`;
    const checkRetryPayment = getStorageItem(keyname);
    if (checkRetryPayment == "1") {
      removeStorageItem(keyname);
      initiatePayment(null, config);
    } else {
      checkPaymentStatus(config);
    } 
    */
  };

  const fetchAppConfig = async () => {
    const partnerid = getStorageItem("partnerId");
    const response = await postWebService("app_config.php", { partnerid });
    const { data } = response;

    setConfigLoaded(true);
    const config = data?.PAYMENT ? JSON.parse(data?.PAYMENT) : null;
    setPaymentConfig(config);
    cbSuccessFetchAppConfig(config);
  };

  const startCountdown = (count = 300) => {
    setCountdown(count);
    setButtonDisabled(true);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          setButtonDisabled(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const navigateTo = (pathname) => history.push({ pathname });

  const cbError = (error) => setIsFormSubmitted(false);

  const cbSuccessPaymentStatus = (data, paymentConfig) => {
    const { code } = data;
    if (code === 404) {
      initiatePayment(data, paymentConfig);
    } else if (code === 403) {
      navigateTo(getRoute("paymentFailed"));
    } else if (code === 200) {
      generatePaymentReceipt();
    } else if (code === 400) {
      setMessage(
        "Payment is pending. Please wait for BillDesk confirmation. After 2 minutes, if the status is still pending, ensure the amount has not been deducted from your account before clicking Re-Initiate Payment."
      );
      initiatePayment(data, paymentConfig);
      startCountdown(120);
    }
  };

  const checkPaymentStatus = async (config) => {
    try {
      const response = await postWebService("settlepayments.php", {
        oid: Number(localStorage.getItem("oid")),
      });

      cbSuccessPaymentStatus(response.data, config);
    } catch (error) {
      cbError(error);
    }
  };

  const generatePaymentReceipt = async () => {
    try {
      const response = await postWebService("paymentconfirmation.php", {
        oid: Number(localStorage.getItem("oid")),
      });

      navigateTo(getRoute("instaConfirmation"));
    } catch (error) {
      cbError(error);
    }
  };

  const cbSuccessCreateOrder = ({
    data: { merchantId, mandateTokenId, bdOrderId, authToken },
  }) => {
    callBilldeskSdk(merchantId, mandateTokenId, bdOrderId, authToken);
    startCountdown(300);
  };

  const createBillDeskOrder = async () => {
    try {
      setErrorMessage("");
      const response = await postWebService("orderscreatebilldesk.php", {
        cid: Number(localStorage.getItem("cid")),
        oid: Number(localStorage.getItem("oid")),
      });

      const { data } = response;

      if (data?.code === 201) {
        togglePaymentBlock("pay_now", true);
        cbSuccessCreateOrder(data);
      } else {
        setErrorMessage(
          data?.msg ?? "Unable to initiate payment. Please try again later."
        );
      }
    } catch (error) {
      cbError(error);
    }
  };

  const retryPayment = async () => {
    try {
      const response = await postWebService("retry-payment.php", {
        oid: Number(localStorage.getItem("oid")),
      });

      setIsFormSubmitted(false);
      const { code } = response.data;
      code == 200 ? window.location.reload() : window.location.reload();
    } catch (error) {
      cbError(error);
    }
  };

  const reinitiatePayment = async () => {
    try {
      setIsFormSubmitted(true);
      const response = await postWebService("settlepayments.php", {
        oid: Number(localStorage.getItem("oid")),
      });

      const { code } = response.data;
      code == 400 || 1 === 1 ? retryPayment() : window.location.reload();
    } catch (error) {
      cbError(error);
    }
  };

  const toggleModal = (keyname = "confirm", flag = false) =>
    setModal({ ...modal, [keyname]: flag });

  const reInitiatePaymentClick = () => toggleModal("confirm", true);

  const handleOnClose = (event, reason, modalKey) => {
    if (Utils.dialogOnCloseReasons.indexOf(reason) === -1) {
      toggleModal(modalKey, false);
    }
  };

  const renderTimer = () => {
    const minutes = Math.floor(countdown / 60);
    const seconds = countdown % 60;
    const formatted = `${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
    return formatted;
  };

  const sendPaymentLink = async (flag = false) => {
    setErrorMessage("");
    setIsFormSubmitted(true);
    setResendPaymentLink(flag);

    const response = await postWebService("createpaymentlink.php", {
      oid: localStorage.getItem("oid"),
    });
    const { data } = response;

    setIsFormSubmitted(false);
    if (data?.code === 201) {
      togglePaymentBlock("payment_link", true);
    } else {
      wrapperToken(false);
      setErrorMessage(data?.msg ?? "Error in generating the payment link.");
    }
  };

  return isLoaded ? (
    <Box className={main}>
      <Box display="flex" justifyContent="center" ml={0} mr={0} mt={3} mb={3}>
        <SectionTitle title={"Payment Section"} />
      </Box>

      {paymentBlock?.options && (
        <Box className={formGroup}>
          {paymentConfig?.pay_now && (
            <Box className={formGroup} textAlign={"left"}>
              <Typography variant="caption">
                <b>Pay Now:</b> Use this option to accept payment through a QR
                code.
              </Typography>
            </Box>
          )}

          {paymentConfig?.pay_now && paymentConfig?.payment_link && (
            <Box
              className={formGroup}
              alignItems={"center"}
              sx={{ width: "100%" }}
            >
              <Typography variant="subtitle1">OR</Typography>
            </Box>
          )}

          {paymentConfig?.payment_link && (
            <Box className={formGroup} textAlign={"left"}>
              <Typography variant="caption">
                <b>Send Payment Link:</b> Use this option to collect payment via
                UPI apps (such as GPay, PhonePe, or Paytm) by sending a payment
                link to the customer through SMS.
              </Typography>
            </Box>
          )}

          <Box className={containerButton}>
            {errorMessage && (
              <Box mb={2}>
                <Typography variant="caption" color="red">
                  {errorMessage}
                </Typography>
              </Box>
            )}
            <CallToAction
              buttonType={"click"}
              errorMessage={errorMessage}
              form={form}
              isDisabled={false}
              isFormSubmitted={isFormSubmitted}
              text={"Pay Now"}
              handleClick={() => createBillDeskOrder()}
            />

            <Box mt={1}>
              <CallToAction
                buttonType={"click"}
                errorMessage={errorMessage}
                form={form}
                isDisabled={false}
                isFormSubmitted={isFormSubmitted}
                text={"Send Payment Link"}
                handleClick={() => sendPaymentLink()}
                color={"secondary"}
              />
            </Box>
          </Box>
        </Box>
      )}

      {paymentBlock?.pay_now && (
        <Box>
          <Box className={main}>
            <Box>
              <Typography variant="caption">{message}</Typography>
            </Box>
            {/* <Box mt={5}>
              <CircularProgress color={"secondary"} />
            </Box> */}
          </Box>

          <Box className={containerButton}>
            <CallToAction
              isDisabled={buttonDisabled || isFormSubmitted}
              handleClick={reInitiatePaymentClick}
              text={
                buttonDisabled
                  ? `Re-Initiate Payment ( ${renderTimer()})`
                  : "Re-Initiate Payment"
              }
            />
          </Box>
        </Box>
      )}

      {paymentBlock?.payment_link && (
        <Box className={formGroup}>
          <Box className={formGroup} textAlign={"left"}>
            <Typography variant="caption">
              {!errorMessage && (
                <Alert icon={false} severity="success">
                  The payment link has been sent
                  {resendPaymentLink ? " again " : ""} to the customer on their
                  registered mobile number. Kindly request the customer to
                  complete the payment using the provided link.
                </Alert>
              )}

              {errorMessage && (
                <Alert icon={false} severity="error">
                  {errorMessage}
                </Alert>
              )}
            </Typography>
          </Box>

          <Box className={containerButton}>
            {/* {errorMessage && (
              <Box mb={2}>
                <Typography variant="caption" color="red">
                  {errorMessage}
                </Typography>
              </Box>
            )} */}
            <CallToAction
              buttonType={"click"}
              errorMessage={errorMessage}
              form={form}
              isDisabled={false}
              isFormSubmitted={isFormSubmitted}
              text={"Check Payment Status"}
              handleClick={() => checkPaymentStatus(paymentConfig)}
            />

            <Box mt={1}>
              <CallToAction
                buttonType={"click"}
                errorMessage={errorMessage}
                form={form}
                isDisabled={false}
                isFormSubmitted={isFormSubmitted}
                text={"Re-Send Payment Link"}
                handleClick={() => sendPaymentLink(true)}
                color={"secondary"}
              />
            </Box>
          </Box>
        </Box>
      )}

      {modal.confirm && (
        <ConfirmationDialog
          modal={modal}
          onClickHandler={reinitiatePayment}
          onClose={handleOnClose}
        />
      )}
    </Box>
  ) : (
    <Box className={loaderBox}>
      <CircularProgress color={"secondary"} />
    </Box>
  );
}

export default withStyles(useStyles, { withTheme: true })(BuyflowPayment);
